<template>
  <div class="performance-dashboard">
    <!-- Skeleton Loader -->
    <performance-skeleton v-if="isLoading" />

    <div v-else>
      <!-- Top Metrics Cards -->
      <b-row class="match-height mb-2">
        <b-col
          v-for="metric in metrics"
          :key="metric.key"
          lg="3"
          sm="6"
        >
          <b-card class="metric-card">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="d-flex align-items-baseline">
                  <h2 class="font-weight-bolder">
                    {{ memberPerformance?.[metric.key] }}
                  </h2>
                  <p
                    v-if="metric.description"
                    class="text-muted small mb-0"
                    style="margin-left: 8px;"
                  >
                    {{ metric.description }}
                  </p>
                </div>
                <p class="text-muted mb-0">
                  {{ metric.label }}
                </p>
              </div>
              <b-avatar
                :variant="metric.variant"
                size="45"
              >
                <feather-icon
                  :icon="metric.icon"
                  size="21"
                />
              </b-avatar>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Middle Section: Charts and Indicators -->
      <b-row class="match-height mb-2">
        <!-- Completion Rate Chart -->
        <b-col
          lg="4"
          md="6"
        >
          <b-card>
            <h5 class="mb-2">
              {{ $t('Completion Rate') }}
            </h5>
            <div class="text-center">
              <vue-apex-charts
                type="donut"
                height="280"
                :options="completionRateOptions"
                :series="[memberPerformance?.completionRate || 0, 100 - (memberPerformance?.completionRate || 0)]"
              />
              <h3 class="font-weight-bold text-primary">
                {{ (memberPerformance?.completionRate || 0).toFixed(2) }}%
              </h3>
              <p class="text-muted">
                {{ $t('complete') }}
              </p>
            </div>
          </b-card>
        </b-col>

        <!-- Tasks by Status Chart -->
        <b-col
          lg="4"
          md="6"
        >
          <b-card>
            <h5 class="mb-2">
              {{ $t('Tasks by Status') }}
            </h5>
            <div
              v-if="tasksByStatusData.length > 0"
              class="text-center"
            >
              <vue-apex-charts
                type="donut"
                height="280"
                :options="tasksByStatusOptions"
                :series="tasksByStatusSeries"
              />
              <div class="mt-2">
                <div
                  v-for="(item, index) in tasksByStatusData"
                  :key="index"
                  class="d-flex justify-content-between align-items-center mb-1"
                >
                  <div class="d-flex align-items-center">
                    <span
                      class="bullet bullet-sm mr-50"
                      :style="{ backgroundColor: tasksByStatusColors[index] }"
                    />
                    <span class="text-muted">{{ item.category }} ({{ item.count }})</span>
                  </div>
                  <span class="text-muted">{{ item.percentage.toFixed(2) }}%</span>
                </div>
              </div>
            </div>
            <div
              v-else
              class="d-flex align-items-center justify-content-center"
              style="min-height: 280px;"
            >
              <p class="text-muted mb-0">
                {{ $t('No data to display') }}
              </p>
            </div>
          </b-card>
        </b-col>

        <!-- Performance Indicators -->
        <b-col
          lg="4"
          md="12"
        >
          <b-card>
            <h5 class="mb-2">
              {{ $t('Performance Indicators') }}
            </h5>

            <div class="mb-2">
              <div class="d-flex justify-content-between mb-50">
                <span>{{ $t('Contribution Score') }}</span>
                <span class="font-weight-bold">{{ memberPerformance?.contributionScore || 0 }}/100</span>
              </div>
              <b-progress
                :value="memberPerformance?.contributionScore || 0"
                :max="100"
                height="8px"
                variant="primary"
                class="mb-1"
              />
            </div>

            <div class="mb-2">
              <div class="d-flex justify-content-between mb-50">
                <span>{{ $t('Activity Level') }}</span>
                <span class="font-weight-bold">{{ memberPerformance?.activityLevel || 0 }}/100</span>
              </div>
              <b-progress
                :value="memberPerformance?.activityLevel || 0"
                :max="100"
                height="8px"
                variant="info"
                class="mb-1"
              />
            </div>

            <div class="mb-2">
              <div class="d-flex justify-content-between mb-50">
                <span>{{ $t('Overall Rating') }}</span>
                <div>
                  <feather-icon
                    v-for="i in 5"
                    :key="i"
                    icon="CircleIcon"
                    size="14"
                    :class="i <= overallRating ? 'text-success' : 'text-muted'"
                    class="fill-current"
                  />
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BCard,
  BRow,
  BCol,
  BAvatar,
  BProgress,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import PerformanceSkeleton from './PerformanceSkeleton.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BAvatar,
    BProgress,
    VueApexCharts,
    PerformanceSkeleton,
  },
  props: {
    member: {
      type: Object,
      required: true,
    },
    timeRange: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      metrics: [
        {
          key: 'totalTasksAssigned', label: this.$t('Tasks Assigned'), icon: 'ListIcon', variant: 'light-primary',
        },
        {
          key: 'totalTasksCompleted', label: this.$t('Tasks Completed'), icon: 'CheckCircleIcon', variant: 'light-success',
        },
        {
          key: 'totalTasksOverdue', label: this.$t('Tasks Overdue'), icon: 'AlertTriangleIcon', variant: 'light-danger',
        },
        {
          key: 'averageCompletionTime', label: this.$t('Completion Time'), description: this.$t('days / task'), icon: 'ClockIcon', variant: 'light-warning',
        },
      ],
      completionRateOptions: {
        chart: {
          type: 'donut',
        },
        colors: ['#00d4bd', '#2d3436'],
        labels: ['Complete', 'Incomplete'],
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '75%',
              labels: {
                show: true,
                name: {
                  show: false,
                },
                value: {
                  show: true,
                  fontSize: '2rem',
                  fontWeight: 600,
                  color: '#00d4bd',
                  formatter: val => `${parseFloat(val).toFixed(2)}%`,
                },
                total: {
                  show: true,
                  label: 'Complete',
                  fontSize: '1rem',
                  color: '#6e6b7b',
                  formatter: () => '',
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 0,
        },
      },
    }
  },
  computed: {
    ...mapGetters('boards', {
      memberPerformance: 'GET_CURRENT_BOARD_MEMBER_PERFORMANCE',
      isLoading: 'GET_LOADING',
    }),
    projectUid() {
      return this.$store.state.project.selectedProject
    },
    boardUid() {
      return this.base58Converter(this.$route.params.boardUid, false)
    },
    tasksByStatusSeries() {
      if (!this.memberPerformance?.tasksByStatus) return []

      return this.memberPerformance.tasksByStatus.map(item => item.count || 0)
    },
    tasksByStatusData() {
      if (!this.memberPerformance?.tasksByStatus) return []

      return this.memberPerformance.tasksByStatus
    },
    tasksByStatusColors() {
      if (!this.memberPerformance?.tasksByStatus) return []

      return this.memberPerformance.tasksByStatus.map(item => item.color || '#00d4bd')
    },
    tasksByStatusLabels() {
      if (!this.memberPerformance?.tasksByStatus) return []

      return this.memberPerformance.tasksByStatus.map(item => item.category || '')
    },
    tasksByStatusOptions() {
      return {
        chart: {
          type: 'donut',
        },
        colors: this.tasksByStatusColors,
        labels: this.tasksByStatusLabels,
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 0,
        },
      }
    },
    overallRating() {
      if (!this.memberPerformance) return 0

      // Calculate rating based on completion rate and contribution score
      const score = (this.memberPerformance.completionRate + this.memberPerformance.contributionScore) / 2
      return Math.round(score / 20) // Convert to 1-5 scale
    },
  },
  watch: {
    timeRange: {
      handler(val) {
        if (val.preset !== 'CUSTOM' || (val.customStart && val.customEnd)) {
          this.getMemberPerformance({
            projectUid: this.projectUid,
            boardUid: this.boardUid,
            memberUid: this.member.uuid,
            timeRange: val,
          })
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.getMemberPerformance({
      projectUid: this.projectUid,
      boardUid: this.boardUid,
      memberUid: this.member.uuid,
      timeRange: this.timeRange,
    })
  },
  methods: {
    ...mapActions('boards', ['getMemberPerformance']),
    formatCompletionTime(days) {
      if (!days) return '0d'
      return `${days.toFixed(1)}d`
    },
  },
}
</script>

<style lang="scss" scoped>
.performance-dashboard {
  .metric-card {
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    }
  }

  .bullet {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .fill-current {
    fill: currentColor;
  }
}

.dark-layout {
  .performance-dashboard {
    .text-muted {
      color: #b4b7bd !important;
    }
  }
}
</style>

<style lang="scss">
.dark-layout {
  .performance-dashboard {
    .apexcharts-legend-text {
      color: #e6e6e6 !important;
    }
    .apexcharts-tooltip {
      .apexcharts-tooltip-title {
        color: #e6e6e6 !important;
      }
    }
  }
}
</style>
